import { z } from 'zod';

// ---------------------------------------------------------------------------
// People directory — the members and teams the signed-in user can pick in the
// Pull requests "People" filter. Served by GET /api/github/org-people?org=… and
// used only to populate the picker; the actual PR fetch takes the chosen logins
// (a team toggle expands to its member logins client-side).
//
// Each member/team is annotated with the org(s) it belongs to, so the picker can
// group people by org and hide anyone unique to a filtered-out org. In an org
// workspace there is one org; in the personal workspace, every org the viewer
// belongs to.
// ---------------------------------------------------------------------------

/** A pickable member, with the orgs (logins) they belong to among those returned. */
export const OrgMemberSchema = z.object({
  login: z.string(),
  orgs: z.array(z.string()),
});
export type OrgMember = z.infer<typeof OrgMemberSchema>;

/** A pickable team: toggling it selects/clears its member logins. */
export const OrgTeamSchema = z.object({
  /** Org-relative team slug. */
  slug: z.string(),
  /** Human-readable team name shown in the picker. */
  name: z.string(),
  /** The org (login) this team belongs to. */
  org: z.string(),
  /** Member logins (first page); a team toggle expands to these. */
  members: z.array(z.string()),
});
export type OrgTeam = z.infer<typeof OrgTeamSchema>;

/** Response envelope for `GET /api/github/org-people`. */
export const OrgPeopleResponseSchema = z.object({
  /** False when the user has not connected a GitHub PAT. */
  connected: z.boolean(),
  /** All org logins present (for the org filter), sorted. */
  orgs: z.array(z.string()),
  /** Pickable members, each with the orgs they belong to. */
  members: z.array(OrgMemberSchema),
  /** Pickable teams (each scoped to one org). */
  teams: z.array(OrgTeamSchema),
  /** True when GitHub reported more members than the first page returned. */
  membersTruncated: z.boolean(),
  /** True when GitHub reported more teams than the first page returned. */
  teamsTruncated: z.boolean(),
});
export type OrgPeopleResponse = z.infer<typeof OrgPeopleResponseSchema>;
